import React from 'react';
import { Box, Chip, InputAdornment, TextField } from '@mui/material';
import { Search } from 'lucide-react';
import { getEventTypeDisplayName } from './PlayByPlayUtils';

export interface PlayByPlaySearchFilterProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  eventTypes: string[];
  selectedEventTypes: Set<string>;
  onToggleEventType: (eventType: string) => void;
}

const PlayByPlaySearchFilter = React.memo(function PlayByPlaySearchFilter({
  searchQuery,
  onSearchChange,
  eventTypes,
  selectedEventTypes,
  onToggleEventType,
}: PlayByPlaySearchFilterProps) {
  return (
    <Box sx={{ p: 1.5}}>
      <TextField
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          width: '100%',
          fontFamily: 'Sohne',
          fontSize: '11px',
          lineHeight: '14px',
          mb: 1,
          '& .MuiOutlinedInput-root': {
            height: '36px',
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.05)',
            borderRadius: '4px',
            padding: '8px 16px',
            gap: '12px',
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.2)',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,255,255,0.3)',
            },
            '& .MuiInputBase-input': {
              padding: 0,
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255,255,255,0.5)',
            opacity: 1,
          },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} color="rgba(255,255,255,0.5)" />
              </InputAdornment>
            ),
          },
        }}
      />

      {eventTypes.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {eventTypes.map((eventType) => {
            const isSelected = selectedEventTypes.has(eventType);
            return (
              <Chip
                key={eventType}
                label={getEventTypeDisplayName(eventType)}
                onClick={() => onToggleEventType(eventType)}
                sx={{
                  fontFamily: 'Sohne',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  cursor: 'pointer',
                  border: '0.5px solid rgba(255,255,255)',
                  color: isSelected ? '#09090C' : 'rgb(255,255,255)',
                  bgcolor: isSelected ? 'rgb(255,255,255)' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'rgba(255,255,255,0.9)' : '#27272E',
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
});

export default PlayByPlaySearchFilter;
